import {IconButton, Snackbar, SnackbarOrigin} from '@mui/material';
import {useState} from 'react';
import './ArrToast.scss';

interface ToastProps {
  id: string;
  title?: string;
  message?: string;
  startIcon?: React.ReactNode;
  closeIcon?: React.ReactNode;
  showStartIcon?: boolean;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  onClose: (id: string, message?: string) => void;
  dismissable?: boolean;
  position?:
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'center'
    | 'bottom-center';
  className?: string;
  closeButtonAriaLabel?: string;
}

const ArrToast: React.FC<ToastProps> = ({
  id,
  title,
  message,
  startIcon,
  closeIcon,
  showStartIcon = false,
  type,
  duration = 5000,
  onClose,
  dismissable = false,
  position = 'top-right',
  className = '',
  closeButtonAriaLabel,
}) => {
  const [open, setOpen] = useState(true);

  const handleClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return;
    setOpen(false);
    onClose(id, message);
  };

  const getAnchorOrigin = (): SnackbarOrigin => {
    switch (position) {
      case 'top-left':
        return {vertical: 'top', horizontal: 'left'};
      case 'top-right':
        return {vertical: 'top', horizontal: 'right'};
      case 'bottom-left':
        return {vertical: 'bottom', horizontal: 'left'};
      case 'bottom-right':
        return {vertical: 'bottom', horizontal: 'right'};
      case 'bottom-center':
        return {vertical: 'bottom', horizontal: 'center'};
      default:
        return {vertical: 'top', horizontal: 'center'};
    }
  };

  return (
    <Snackbar
      open={open}
      className={`${showStartIcon ? 'arr-toast__content' : 'arr-toast__content-action'} ${className}`}
      autoHideDuration={duration}
      onClose={handleClose}
      anchorOrigin={getAnchorOrigin()}
      aria-labelledby={title}
      aria-describedby={message}
      role="alert"
      aria-atomic="true"
    >
      <div
        className={`arr-toast arr-toast--${type} ${className}`}
        data-testid="toast-box"
      >
        <div
          className={
            showStartIcon ? 'arr-toast__content' : 'arr-toast__content-action'
          }
          data-testid={`arr-toast-div`}>
          {showStartIcon && startIcon}
          <div
            className={message ? 'arr-toast__message-section' : ''}
            data-testid={`arr-toast-div-btn`}>
            {title && (
              <div
                className="arr-toast__title title-typography"
                data-testid="toast-title"
              >
                {title}
              </div>
            )}
            {message && (
              <div
                className="arr-toast__message typo-text body3_size"
                data-testid="toast-message"
              >
                {message}
              </div>
            )}
          </div>
          {dismissable && (
            <IconButton
              className="arr-toast__close"
              onClick={() => onClose(id, message)}
              aria-label={closeButtonAriaLabel ? closeButtonAriaLabel : 'Close'}
              data-testid="toast-close"
            >
              {closeIcon}
            </IconButton>
          )}
        </div>
      </div>
    </Snackbar>
  );
};

export default ArrToast;
